import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { clientsProposes, iconeAttestation, nomAttestation } from '../../lib/attestations';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * La fenêtre d'envoi des attestations cochées dans leur carte : à qui (un
 * client, ou n'importe quelle adresse — une régie, un particulier), le
 * message, relu et modifiable, et les documents joints. Le message part de
 * l'adresse de l'entreprise, comme les offres et les factures.
 *
 * @param {object} props propriétés
 * @param {object} props.soc l'entreprise (son nom signe le message)
 * @param {object[]} props.pieces les attestations cochées
 * @param {object[]} [props.clients] les clients, pour les retrouver par leur nom
 * @param {(id: number) => void} props.onRetire décoche une attestation
 * @param {() => void} props.onClose fermeture de la fenêtre
 * @param {() => void} [props.onEnvoye] appelé une fois le message parti, à la fermeture
 * @param {() => Promise<object>} [props.chargeBoite] dit de quelle adresse il part
 * @param {(envoi: object) => Promise<object>} [props.envoie] l'envoi au serveur
 * @returns {JSX.Element} la fenêtre
 */
export function EnvoiAttestations({
  soc,
  pieces,
  clients,
  onRetire,
  onClose,
  onEnvoye,
  chargeBoite = () => envoisApi.boite(),
  envoie = (envoi) => envoisApi.envoieAttestations(envoi),
}) {
  const [qui, setQui] = useState('');
  // Le message prêt à partir, une phrase par ligne : chacune se traduit seule.
  const [message, setMessage] = useState(() =>
    [
      tr('Bonjour,'),
      '',
      tr('Vous trouverez ci-joint les documents demandés.'),
      '',
      tr('Meilleures salutations,'),
      soc?.nom || '',
    ]
      .join('\n')
      .trim(),
  );
  const [etat, setEtat] = useState('repos');
  const [retour, setRetour] = useState('');
  const [boite, setBoite] = useState(null);

  // La boîte de l'entreprise, si elle est raccordée : le message part de son
  // adresse (info@…), et la fenêtre le dit.
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(chargeBoite)
      .then((b) => {
        if (vivant && b?.raccordee) setBoite(b);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
    // Une lecture à l'ouverture de la fenêtre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adresse = qui.trim();
  const proposes = clientsProposes(clients, qui);
  const n = pieces.length;
  const pret = n > 0 && EMAIL.test(adresse) && etat !== 'envoi' && etat !== 'envoye';

  const envoieMaintenant = async () => {
    if (!pret) return;
    setEtat('envoi');
    setRetour('');
    try {
      const ligne = await envoie({
        email: adresse,
        message: message.trim(),
        attestations: pieces.map((p) => p.id),
      });
      setEtat('envoye');
      setRetour(trf('Envoyé à {0}', ligne?.destinataire || adresse));
      // Le message est parti : la fenêtre se referme, le temps de lire, et
      // les cases se décochent.
      setTimeout(() => {
        if (onEnvoye) onEnvoye();
        onClose();
      }, 1200);
    } catch (e) {
      setEtat('echec');
      setRetour(e?.message || tr('L’envoi a échoué.'));
    }
  };

  const etiquette = {
    fontSize: 10,
    fontWeight: 800,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    margin: '12px 0 5px',
  };
  const champ = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 11px',
    borderRadius: 10,
    border: '1px solid ' + C.border,
    fontSize: 13,
    fontFamily: 'inherit',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        role="dialog"
        aria-label={tr('Envoyer les attestations')}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: '16px 16px 0 0',
          padding: 18,
          width: '100%',
          maxWidth: 440,
          maxHeight: '92vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 900, color: C.ink }}>
          ✉️ {n === 1 ? tr('Envoyer 1 document') : trf('Envoyer {0} documents', n)}
        </div>

        <div style={etiquette}>{tr('À qui ?')}</div>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          aria-label={tr('À qui ?')}
          placeholder={tr('🔎 Un client, ou tape une adresse e-mail…')}
          value={qui}
          onChange={(e) => setQui(e.target.value)}
          style={champ}
        />
        {proposes.map((c) => (
          <button
            key={c.id ?? c.email}
            type="button"
            onClick={() => setQui(String(c.email).trim())}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginTop: 5,
              padding: '8px 10px',
              borderRadius: 9,
              border: '1px solid ' + C.border,
              background: C.navyXL,
              fontSize: 12,
              fontWeight: 700,
              color: C.teal,
              cursor: 'pointer',
            }}
          >
            {c.nom} · {c.email}
          </button>
        ))}

        <div style={etiquette}>{tr('Message')}</div>
        <textarea
          aria-label={tr('Message')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          style={{ ...champ, resize: 'vertical', lineHeight: 1.45 }}
        />

        <div style={etiquette}>{trf('Joints ({0})', n)}</div>
        {pieces.map((p) => (
          <div
            key={p.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px dashed ' + C.border,
              fontSize: 12.5,
              color: C.ink,
            }}
          >
            <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {iconeAttestation(p.categorie)} {tr(nomAttestation(p, soc?.forme))}
            </span>
            <button
              type="button"
              aria-label={tr('Retirer')}
              onClick={() => onRetire(p.id)}
              style={{
                border: 'none',
                background: 'none',
                fontSize: 15,
                color: C.grey,
                cursor: 'pointer',
                padding: '2px 6px',
              }}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          disabled={!pret}
          onClick={envoieMaintenant}
          style={{
            width: '100%',
            marginTop: 14,
            padding: '13px',
            borderRadius: 12,
            border: 'none',
            background: C.green,
            color: '#fff',
            fontSize: 14,
            fontWeight: 900,
            cursor: pret ? 'pointer' : 'default',
            opacity: pret ? 1 : 0.45,
          }}
        >
          {etat === 'envoi' ? tr('Envoi…') : '✉️ ' + tr('Envoyer')}
        </button>
        {retour && (
          <div
            role={etat === 'echec' ? 'alert' : 'status'}
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              marginTop: 8,
              color: etat === 'echec' ? C.red : C.green,
            }}
          >
            {etat === 'echec' ? '✕ ' : '✓ '}
            {retour}
          </div>
        )}
        <div style={{ fontSize: 10, color: C.muted, textAlign: 'center', marginTop: 8 }}>
          {boite?.adresse
            ? trf('Part de ton adresse ({0}), comme tes offres et factures', boite.adresse)
            : tr('Part de l’adresse de l’entreprise, comme tes offres et factures')}
        </div>
        <div
          role="button"
          onClick={onClose}
          style={{
            textAlign: 'center',
            padding: 11,
            fontSize: 11.5,
            fontWeight: 700,
            color: C.muted,
            cursor: 'pointer',
          }}
        >
          {tr('Annuler')}
        </div>
      </div>
    </div>
  );
}
